import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { 
  Bell, 
  CheckCheck, 
  Settings, 
  Wifi, 
  WifiOff, 
  Clock,
  AlertTriangle,
  Info,
  Package,
  Crown,
  Shield,
  Filter,
  X
} from 'lucide-react';
import { useReliableNotifications } from '@/hooks/useReliableNotifications';
import { useMasterNotifications } from '@/hooks/notifications/useMasterNotifications';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';
import type { AdvancedNotification } from '@/types/notifications';

interface UnifiedNotificationCenterProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const UnifiedNotificationCenter: React.FC<UnifiedNotificationCenterProps> = ({
  isMobile = false,
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isConnected, reconnectAttempts, forceReconnect } = useReliableNotifications();
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    deleteNotification 
  } = useMasterNotifications();
  const { user, isSuperAdmin, hasAdminAccess, userRole, loading } = useOptimizedAuth();
  const { isMobile: responsiveIsMobile } = useResponsive();

  const isOpen = controlledOpen ?? internalOpen;
  const onOpenChange = controlledOnOpenChange ?? setInternalOpen;
  const shouldUseMobile = isMobile || responsiveIsMobile;
  
  // Handle authentication errors gracefully
  const hasAuthError = !user && !loading;

  // Group notifications by priority and type
  const groupedNotifications = useMemo(() => {
    const groups = {
      urgent: notifications.filter(n => n.priority === 4),
      high: notifications.filter(n => n.priority === 3),
      medium: notifications.filter(n => n.priority === 2),
      low: notifications.filter(n => n.priority === 1),
      unread: notifications.filter(n => n.persistent), // Use persistent as proxy for unread
      today: notifications.filter(n => {
        const today = new Date();
        const notifDate = new Date(n.timestamp);
        return notifDate.toDateString() === today.toDateString();
      })
    };

    return groups;
  }, [notifications]);

  const getFilteredNotifications = () => {
    switch (activeTab) {
      case 'unread':
        return groupedNotifications.unread;
      case 'urgent':
        return groupedNotifications.urgent;
      case 'today':
        return groupedNotifications.today;
      default:
        return notifications;
    }
  };

  const getNotificationIcon = (notification: AdvancedNotification) => {
    switch (notification.type) {
      case 'order_created':
      case 'order_updated':
        return <Package className="h-4 w-4 text-blue-500" />;
      case 'order_payment_failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'system_error':
      case 'security_alert':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 4: return 'border-red-500 bg-red-50 dark:bg-red-950';
      case 3: return 'border-orange-500 bg-orange-50 dark:bg-orange-950';
      case 2: return 'border-blue-500 bg-blue-50 dark:bg-blue-950';
      default: return 'border-border bg-card';
    }
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleNotificationClick = async (notification: AdvancedNotification) => {
    await markAsRead(notification.id);
    
    if (notification.metadata?.action_url) {
      const url = notification.metadata.action_url;
      if (url?.startsWith('/')) {
        window.location.href = url;
      } else if (url) {
        window.open(url, '_blank');
      }
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings/notifications');
    onOpenChange(false);
    toast({
      title: "Notification Settings",
      description: "Redirecting to notification preferences...",
    });
  };

  const NotificationItem = ({ notification }: { notification: AdvancedNotification }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        "p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md",
        getPriorityColor(notification.priority || 2),
        notification.persistent && "ring-2 ring-primary/20"
      )}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="flex items-start gap-3">
        {getNotificationIcon(notification)}
        <div className="flex-1 space-y-1">
          <div className="flex items-start justify-between">
            <h4 className={cn(
              "text-sm font-medium",
              notification.persistent && "font-semibold"
            )}>
              {notification.title}
            </h4>
            <div className="flex items-center gap-2">
              {notification.persistent && (
                <div className="w-2 h-2 bg-primary rounded-full" />
              )}
              <span className="text-xs text-muted-foreground">
                {formatTimeAgo(notification.timestamp)}
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
          {notification.priority === 4 && (
            <Badge variant="destructive" className="text-xs">
              Urgent
            </Badge>
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            // Snooze functionality to be implemented
          }}
          className="text-xs"
        >
          <Clock className="h-3 w-3 mr-1" />
          Snooze
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            deleteNotification(notification.id);
          }}
          className="text-xs text-red-600 hover:text-red-700"
        >
          <X className="h-3 w-3 mr-1" />
          Dismiss
        </Button>
      </div>
    </motion.div>
  );

  const ConnectionStatus = () => (
    <div className="flex items-center gap-2">
      {isConnected ? (
        <Wifi className="h-4 w-4 text-green-500" />
      ) : (
        <WifiOff className="h-4 w-4 text-red-500" />
      )}
      <span className="text-xs text-muted-foreground">
        {isConnected 
          ? 'Connected' 
          : reconnectAttempts > 0 
            ? `Reconnecting (${reconnectAttempts})` 
            : 'Disconnected'
        }
      </span>
      {!isConnected && (
        <Button variant="ghost" size="sm" onClick={forceReconnect} className="text-xs">
          Retry
        </Button>
      )}
    </div>
  );

  const RoleIndicator = () => (
    <>
      {(isSuperAdmin || hasAdminAccess) && (
        <div className="flex items-center gap-2">
          <Badge variant={isSuperAdmin ? "destructive" : "secondary"} className="text-xs">
            {isSuperAdmin ? <Crown className="h-3 w-3 mr-1" /> : <Shield className="h-3 w-3 mr-1" />}
            {isSuperAdmin ? 'Super Admin' : userRole}
          </Badge>
        </div>
      )}
    </>
  );

  const NotificationTabs = () => (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="all" className="text-xs">
          All ({notifications.length})
        </TabsTrigger>
        <TabsTrigger value="unread" className="text-xs">
          Unread ({groupedNotifications.unread.length})
        </TabsTrigger>
        <TabsTrigger value="urgent" className="text-xs">
          Urgent ({groupedNotifications.urgent.length})
        </TabsTrigger>
        <TabsTrigger value="today" className="text-xs">
          Today ({groupedNotifications.today.length})
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );

  // Mobile full-screen view
  if (shouldUseMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-background"
          >
            <div className="flex flex-col h-full">
              {/* Mobile Header */}
              <div className="flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  <h1 className="text-lg font-semibold">Notifications</h1>
                  {unreadCount > 0 && (
                    <Badge variant="destructive">{unreadCount}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <RoleIndicator />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleSettingsClick}
                          className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Notification Settings</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                   {unreadCount > 0 && (
                     <Button variant="outline" size="sm" onClick={() => {/* Mark all as read to be implemented */}}>
                       <CheckCheck className="h-3 w-3 mr-1" />
                       Mark all read
                     </Button>
                   )}
                  <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Mobile Connection Status */}
              <div className="px-4 py-2 border-b bg-muted/50">
                <ConnectionStatus />
              </div>

              {/* Mobile Tabs */}
              <div className="px-4 py-2 border-b">
                <NotificationTabs />
              </div>

              {/* Mobile Content */}
              <div className="flex-1 overflow-hidden">
                {getFilteredNotifications().length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground px-6">
                    <Bell className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-xl mb-2">No notifications</p>
                    <p className="text-sm text-center">
                      You'll see notifications here when they arrive
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="h-full">
                    <div className="space-y-3 p-4">
                      <AnimatePresence>
                        {getFilteredNotifications().map((notification) => (
                          <NotificationItem key={notification.id} notification={notification} />
                        ))}
                      </AnimatePresence>
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Desktop popover view
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs animate-pulse rounded-full min-w-[20px]"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Bell className="h-4 w-4" />
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="secondary">{unreadCount} unread</Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowFilters(!showFilters)}
                        className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-200"
                      >
                        <Filter className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Filter notifications</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleSettingsClick}
                        className="text-muted-foreground hover:text-foreground hover:bg-accent hover:scale-105 transition-all duration-200 group"
                      >
                        <Settings className="h-3 w-3 group-hover:rotate-90 transition-transform duration-200" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Notification Settings</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-xs">
              <ConnectionStatus />
              <RoleIndicator />
            </div>

            {unreadCount > 0 && (
               <Button 
                 variant="outline" 
                 size="sm" 
                 onClick={() => {/* Mark all as read to be implemented */}}
                 className="text-xs"
               >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
          </CardHeader>

          {showFilters && (
            <div className="px-4 pb-3">
              <NotificationTabs />
            </div>
          )}

          <CardContent className="p-0">
            <ScrollArea className="h-96">
              {getFilteredNotifications().length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications</p>
                  <p className="text-xs mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-2 p-3">
                  <AnimatePresence>
                    {getFilteredNotifications().map((notification) => (
                      <NotificationItem key={notification.id} notification={notification} />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
};

export default UnifiedNotificationCenter;